import { useView } from '../hooks';

import {
  PractitionerLocationDetails,
  SearchHeader,
  ViewToggle,
} from '~/components';

import { useAppSelector } from '~/state';
import { selectLocation } from '~/state/location';
import { selectDiscipline } from '~/state/discipline';
import { useEffect, ReactElement } from 'react';
import { selectSelectedPractitioner } from '~/state/activeSelection';
import { SearchResultList, MapContainer } from '~/views';
import { selectAvailability } from '~/state/searchFilters';
import { trackPageView, useSyncReduxToUrlParams } from '~/hooks';
import { Views } from '~/utilities';
import cn from 'classnames';
import { hasFeatureFlag } from '@janeapp/feature-flags-client';
import noPractitionersFoundIcon from '~/assets/images/no-practitioners-found.png';
import { useSearchPractitioners } from '~/queries/useSearchPractitioners';
import { selectMapBounds } from '~/state/mapData';
interface SearchProps {
  locationInput: ReactElement;
}

export const Search = ({ locationInput }: SearchProps) => {
  const { view, handleViewChange } = useView();
  useSyncReduxToUrlParams();
  const discipline = useAppSelector(selectDiscipline);
  const location = useAppSelector(selectLocation);
  const selectedPractitioner = useAppSelector(selectSelectedPractitioner);
  const availability = useAppSelector(selectAvailability);
  const mapBounds = useAppSelector(selectMapBounds);

  const { results, fetchNextPage, hasNextPage, isFetching, isError, refetch } =
    useSearchPractitioners({ discipline, location, availability, mapBounds });

  const searchResultsReturned = Boolean(results.length);
  const getGridContainerClass = () => {
    if (selectedPractitioner) return 'grid-container has-details';
    if (searchResultsReturned) return 'grid-container';
    return 'grid-container-no-toggle';
  };

  useEffect(() => {
    trackPageView('mpl_search_page_viewed', { path: window.location.pathname });
  }, []);

  // Reset scroll position when navigating to search page
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const listContainerClasses = cn(
    'grid-area-list flex-col overflow-hidden md:border-r md:border-neutral-30',
    view !== Views.Map && !selectedPractitioner ? 'flex' : 'hidden'
  );

  const hasSearched = Boolean(discipline && location);
  const loading = isFetching ? 'pending' : isError ? 'failed' : 'succeeded';

  const getMapContainer = () => {
    if (hasFeatureFlag('NORTH_VANCOUVER_BETA')) {
      return discipline && location ? (
        <>
          <MapContainer view={view} results={results} loading={loading} />
          <ViewToggle currentView={view} onToggle={handleViewChange} />
        </>
      ) : (
        <div className="grid-area-map flex h-full w-full items-center justify-center">
          <img
            src={noPractitionersFoundIcon}
            alt="Illustration showing no search results found"
            className="h-40 w-40"
          />
        </div>
      );
    }

    return (
      <>
        <MapContainer view={view} results={results} loading={loading} />
        {searchResultsReturned && (
          <ViewToggle currentView={view} onToggle={handleViewChange} />
        )}
      </>
    );
  };

  return (
    <div
      data-testid="search-page"
      className="mobile-safe-area-insets h-dvh w-dvw"
    >
      <div
        data-testid="grid-container"
        className={`${getGridContainerClass()} h-full w-full`}
      >
        {selectedPractitioner && (
          <PractitionerLocationDetails result={selectedPractitioner} />
        )}
        <SearchHeader
          discipline={discipline}
          location={location}
          locationInput={locationInput}
        />
        <div data-testid="search-result-list" className={listContainerClasses}>
          <SearchResultList
            hasMore={hasNextPage}
            hasSearched={hasSearched}
            loading={loading}
            results={results}
            onRetry={refetch}
            onLoadMore={fetchNextPage}
          />
        </div>
        {getMapContainer()}
      </div>
    </div>
  );
};
